import { IDatasourceUsageInfo } from '../ServerApi/types/IUsageInfo';
import { IDataConnectionInfoProperties } from './IDataConnectionInfo';
import { IDataQualityIndicatorItemInfo } from './IDataQualityIndicatorItemInfo';
import { IRemoteRefreshPropertiesInfo } from './IRemoteRefreshPropertiesInfo';
import { IWorkbookInfo } from './IWorkbookInfo';
import {
  ICertifiableItemInfo,
  ICreatedItemInfo,
  IDescriptionItemInfo,
  IDownloadItemInfo,
  IExplorableItemTypeInfo,
  IExtractItemInfo,
  IFavoriteItem,
  IIdItemInfo,
  IItemStatisticsInfo,
  IModifiedByItemInfo,
  INamedItemInfo,
  IOwnedItemInfo,
  IProjectItemInfo,
  ILocationItemInfo,
  ISpaceTypeInfo,
  ITaggedItemInfo,
  IUpdatedItemInfo,
  ILuidItemInfo,
} from './resourceInfoParts';

export interface IDataSourceBasicInfo extends
  INamedItemInfo,
  IOwnedItemInfo,
  IModifiedByItemInfo,
  IProjectItemInfo,
  ILocationItemInfo,
  ILuidItemInfo
{
  workbook?: INamedItemInfo;
  datasourceLuid?: string; // defined for data source returned from CRASS/content-exploration
}

export interface IPublishedDatasourceInfoProperties{
  hasExtracts: boolean;
  lastRefreshedAt?: string;
  hasEncryptableExtracts: boolean;
  connectedWorkbooksCount: number;
  extractEncryptionState: string;
}

export interface IDataSourceBase extends
  IDataSourceBasicInfo,
  IDataConnectionInfoProperties,
  IDownloadItemInfo,
  IExtractItemInfo,
  IFavoriteItem,
  ITaggedItemInfo,
  IUpdatedItemInfo,
  IDataQualityIndicatorItemInfo,
  IExplorableItemTypeInfo<'datasource'>,
  ISpaceTypeInfo<'datasource'>
{
  certificationDetails: ICertifiableItemInfo;
  publishedDatasourceDetails?: IPublishedDatasourceInfoProperties;

  // Uniquely identifies a datasource on a site.
  // It is essentially he name of the data source,  normalized and encoded to be suitable for use in path of a URL.
  // (AKA "repositoryUrl" on the backend)
  urlName: string;
  connectedWorkbooksCount: number;
  dataConnectionsCount: number;

  // the "idForFavoriting" field exists while the backend transitions away from UnifiedDatasources.
  // It is used only in special cases (currently only favoriting and finding data connections by
  // data source) where the backend can't map the unified datasource ID to the published datasource
  // ID. In the future, once datasource IDs have been standardized, these use cases will use the
  // data source's "id" field, same as in the rest of the app.
  idForFavoriting: string;

  isHierarchical: boolean;
  nlpSetting: NlpSetting;
  askDataIndexing: AskDataIndexing;
  askDataFeedbackToCuratorEnabled: boolean;

  isExtractOnly: boolean;
  isFileBased: boolean;
}

export interface IDataSourceInfo extends
  IDataSourceBase,
  IItemStatisticsInfo<IDatasourceUsageInfo>,
  IDescriptionItemInfo
{
}

export interface IDataSourceDetailInfo extends
  IDataSourceBase,
  ICreatedItemInfo,
  IDescriptionItemInfo,
  IRemoteRefreshPropertiesInfo
{
  connectedUsersCount: number;
  workbook?: IWorkbookInfo;
}

export interface IDataSourceDetailInfoWithUsage extends
  IDataSourceDetailInfo,
  IItemStatisticsInfo<IDatasourceUsageInfo>
{
}

export enum ExtractEncryptionState {
  unencrypted = 'unencrypted',
  encrypted = 'encrypted',
  pendingEncryption = 'pendingEncryption',
  pendingDecryption = 'pendingDecryption'
}

export enum NlpSetting {
  enabled = 'enabled',
  disabled = 'disabled',
  disableIndex = 'disable_index_values',
  refresh24Hrs = 'refresh_24hrs',
  siteDefault = 'site_default',
}

export enum AskDataIndexing {
  manual = 'MANUAL',
  automatic = 'AUTOMATIC',
  disabled = 'DISABLED',
}

export enum ExtractCreationState {
  pending = 'pendingCreation'
}

export interface IDataSourceMiniInfo extends
  INamedItemInfo,
  IIdItemInfo
{
}
